import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  Upload as UploadIcon,
  Refresh as RefreshIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import {
  useUploadReviewFile,
  useReviewList,
} from '../../../services/bulkPricingReview/bulkPricingReview.service';
import { ReviewObjectDto } from '../../../services/bulkPricingReview/bulkPricingReview.types';
import { useUser } from '../../../services/auth/auth.service';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'archived', label: 'Archived' },
  { value: 'failed', label: 'Failed' },
  { value: 'canceled', label: 'Canceled' },
];

interface BulkUploadWorkflowProps {
  selectedFranchises?: any[]; // Use actual franchise type from your app
  onReviewSelected?: (review: ReviewObjectDto) => void; // Callback when a review is selected for detailed view
  onUploadComplete?: () => void; // Callback when upload is completed
}

const BulkUploadWorkflow: React.FC<BulkUploadWorkflowProps> = ({
  selectedFranchises = [],
  onReviewSelected,
  onUploadComplete,
}) => {  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [franchiseFilter, setFranchiseFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('pending'); // Default to pending
  
  // Get current user for authentication
  const { data: userData } = useUser(true);
  const currentUser = userData?.data;
  
  // Mutations
  const uploadMutation = useUploadReviewFile();
  
  // Get reviews using the new endpoint
  const {
    data: reviewListResponse,
    isLoading: reviewsLoading,
    refetch: refetchReviews,
  } = useReviewList(franchiseFilter || undefined, statusFilter || undefined, true);  const reviews = reviewListResponse?.data?.objects || [];
    const handleReviewClick = useCallback((review: ReviewObjectDto) => {
    onReviewSelected?.(review);
  }, [onReviewSelected]);

  const handleFileSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files.length > 0) {
      const file = files[0];
      // Strict CSV validation - only CSV files are allowed
      if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
        setSelectedFile(file);
        setUploadError(null);
      } else {
        setUploadError('Only CSV files are allowed');
        setSelectedFile(null);
        // Reset file input
        event.target.value = '';
      }
    }
  }, []);

  const handleUpload = useCallback(async () => {
    if (!selectedFile) {
      setUploadError('Please select a file');
      return;
    }

    if (!currentUser?.loginId) {
      setUploadError('User not authenticated. Please log in again.');
      return;
    }

    // Clear any previous validation errors before starting upload
    setUploadError(null);

    try {
      await uploadMutation.mutateAsync({ 
        file: selectedFile, 
        userId: currentUser.loginId 
      });
      setSelectedFile(null);
      refetchReviews();
      onUploadComplete?.();
    } catch (error: any) {
      // Handle specific new error scenarios
      let errorMessage = 'Upload failed';
      
      if (error?.response?.status === 400) {
        if (error.response?.data?.message?.includes('X-User-Id header is required')) {
          errorMessage = 'User ID is required for upload';
        } else if (error.response?.data?.message?.includes('Only CSV files are allowed')) {
          errorMessage = 'Only CSV files are allowed';
        } else {
          errorMessage = error.response?.data?.message || errorMessage;
        }
      } else {
        errorMessage = error?.message || errorMessage;
      }
      
      setUploadError(errorMessage);
    }
  }, [selectedFile, uploadMutation, refetchReviews, onUploadComplete, currentUser]);
  // Extract franchise code from file path
  const extractFranchiseFromPath = (filePath: string): string => {
    // Path format can be either:
    // "moa-bulk-review/FRANCHISE_CODE/filename.json" (for pending)
    // OR "moa-bulk-review/STATUS/FRANCHISE_CODE/filename" (for archive, canceled, failed)
    const parts = filePath.split('/');
    if (parts.length >= 3) {
      // Check if parts[1] is one of the actual S3 status directories
      const possibleStatus = parts[1].toLowerCase();
      if (['archive', 'canceled', 'failed'].includes(possibleStatus)) {
        // Status is included in path, franchise code is at parts[2]
        return parts[2];
      }
    }
    // Default case: franchise code is at parts[1] (pending files)
    return parts.length >= 2 ? parts[1] : '';
  };

  const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
    switch (status.toLowerCase()) {
      case 'uploaded':
        return 'info'; // New initial status after file upload
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'processing':
      case 'in_progress':
        return 'primary';
      case 'pending':
        return 'info';
      case 'canceled':
      case 'cancelled':
        return 'warning';
      case 'archived':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };  return (
    <Box>
      {/* File Upload Section */}
      <Card sx={{ mb: 3 }} data-testid="bulkUploadCard">
        <CardHeader title="Upload Bulk Operations CSV File" />
        <Divider />
        <CardContent>          <Box sx={{ border: '2px dashed #ccc', borderRadius: 2, p: 3, textAlign: 'center' }}>
            <AttachFileIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              Select CSV File
            </Typography>            <Typography variant="body2" color="text.secondary" gutterBottom>
              Choose a CSV file containing bulk pricing operations. The file will be processed and split by franchise automatically.
            </Typography>
            <Button
              component="label"
              variant="outlined"
              startIcon={<AttachFileIcon />}
              sx={{ mt: 2 }}
            >
              Browse Files
              <VisuallyHiddenInput
                type="file"
                accept=".csv,text/csv"
                onChange={handleFileSelect}
              />
            </Button>
          </Box>

          {selectedFile && (
            <Box sx={{ mt: 2 }}>
              <Alert severity="info">
                Selected file: {selectedFile.name} ({formatFileSize(selectedFile.size)})
              </Alert>
            </Box>
          )}

          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}

          {uploadMutation.isError && !uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadMutation.error instanceof Error ? uploadMutation.error.message : 'Upload failed'}
            </Alert>
          )}          {uploadMutation.isSuccess && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Bulk file uploaded successfully!
            </Alert>
          )}

          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              onClick={handleUpload}
              disabled={!selectedFile || uploadMutation.isLoading}
            >
              {uploadMutation.isLoading ? 'Uploading...' : 'Upload File'}            </Button>          </Box>
        </CardContent>
      </Card>

      {/* Jobs Table */}
      <Card>
        <CardHeader 
          title="Bulk Jobs" 
          subheader="View and manage bulk pricing jobs"
          action={
            <Button
              startIcon={<RefreshIcon />}
              onClick={() => refetchReviews()}
              disabled={reviewsLoading}
            >
              Refresh
            </Button>
          }
        />
        <Divider />
        <CardContent>
          {/* Filters */}
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <TextField
              label="Franchise Filter"
              value={franchiseFilter}
              onChange={(e) => setFranchiseFilter(e.target.value)}
              placeholder="Enter franchise code (optional)"
              size="small"
              sx={{ minWidth: 200 }}
            />
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {reviewsLoading && <LinearProgress />}
          
          {reviews.length === 0 ? (
            <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
              {reviewsLoading ? 'Loading jobs...' : 'No bulk jobs found with the current filters.'}
            </Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>File Name</TableCell>
                    <TableCell>Franchise</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Size</TableCell>
                    <TableCell>Last Modified</TableCell>
                    <TableCell align="center">Select</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reviews.map((review: ReviewObjectDto) => {
                    const fileName = review.key.split('/').pop() || review.key;
                    const franchiseCode = extractFranchiseFromPath(review.key);
                    
                    return (
                      <TableRow key={review.key}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">
                            {fileName}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {franchiseCode}
                          </Typography>
                        </TableCell>
                        <TableCell>                          <Chip
                            label={review.status}
                            color={getStatusColor(review.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatFileSize(review.size)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(review.lastModified).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => handleReviewClick(review)}
                          >
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BulkUploadWorkflow;
